import { asObjecttypeIdString, isValidMainObjecttype } from 'cadenza/repository/model/objecttype-id.gen';
import { getStore } from 'cadenza/store/cadenza-store';
import { canTheoreticallyDoDataAcquisitionOnObjecttype } from 'cadenza/workbook/acquisition/data-acquisition-action-utils';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';
import type { AttributeBundleDefinitionDto } from 'cadenza/workbook/model/attribute-bundle-definition-dto';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import {
  getMainObjecttypeIdForDataView,
  getObjecttypeById,
  getVisualizationKeyByDataViewId,
} from 'cadenza/workbook/workbook-core-selectors.state';
import type {
  AttributeBundleValidationResult,
  IAttributeBundleValidator,
} from 'cadenza/workbook/workbook-panel/settings/attributes/attribute-bundle-validator';

import i18n from './writeback-table-attribute-bundle-validator.properties';

// See WritebackTableAttributeBundleValidator.java
export class WritebackTableAttributeBundleValidator implements IAttributeBundleValidator {
  readonly #dataViewId: DataViewId;

  constructor(dataViewId: DataViewId) {
    this.#dataViewId = dataViewId;
  }

  validate(
    bundleDefinition: AttributeBundleDefinitionDto,
    attribute: WorkbookAttributeDto,
  ): AttributeBundleValidationResult | undefined {
    if (this.#isWritebackTable() && bundleDefinition.attributeBundleName === 'INDIVIDUAL_COLUMNS') {
      if (
        !canTheoreticallyDoDataAcquisitionOnObjecttype(
          getStore().getState(),
          attribute.objecttypeId,
          'ACQUIRE_TABLE_DATA',
        )
      ) {
        return { accepts: false, message: i18n('<WRITEBACK_INCOMPATIBLE_WITH_ACQUISITION>') };
      }
      const mainObjecttypeId = getMainObjecttypeIdForDataView(getStore().getState(), this.#dataViewId);
      const mainObjecttypeIdString = isValidMainObjecttype(mainObjecttypeId) && asObjecttypeIdString(mainObjecttypeId);
      if (mainObjecttypeIdString && attribute.objecttypeId !== mainObjecttypeIdString) {
        // check if there is an ONE_TO_MANY association between the main objecttype and the attribute objecttype
        const attributeObjecttype = getObjecttypeById(getStore().getState(), attribute.objecttypeId);
        const foundAssociation = attributeObjecttype.objecttypeAssociations?.associations?.some(
          (association) =>
            association.cardinality === 'MANY_TO_ONE' && association.otherObjecttypeName === mainObjecttypeId.itemName,
        );
        if (!foundAssociation) {
          return { accepts: false, message: i18n('<WRITEBACK_MULTIPLE_OBJECTTYPES_WITH_NO_ASSOCIATION>') };
        }
      }
    }
  }

  #isWritebackTable() {
    return (
      this.#dataViewId && getVisualizationKeyByDataViewId(getStore().getState(), this.#dataViewId) === 'TABLE.WRITEBACK'
    );
  }
}
